import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { MainStylesVars, StyleSheet } from '../stylesheet'
import { useTranslation } from 'react-i18next'
import { EventsPresentationLists } from './list-by-places'
import { EventsTimelineVue } from './events-timeline'
import type { Category, DateDay, Genre, GenreId, GuestTypeId, Program, ProgramId, Scene, SceneId } from '../../../grab/types'
import type { StaticMainConfig, PageScheduleSettings } from '../chapito-static'
import { MainConfig, PageScheduleConfig, useLang } from '../chapito-static'
import { Button, ConfigProvider, Dropdown, Input, Select } from 'antd'
import { Icon, IconBtn } from '../components/components-lib'
import { Title } from '../components/title'
import { LangSwitchDropdown } from '../components/lang-switch-dropdown'
import { GuestsPresentationList } from './guests'
import {
  type EventsSortOrder,
  type GlobalQuery,
  type GuestSortOrder,
  type HomePageView,
  Navigation,
  getURLParamsForGlobalQuery,
  loadGlobalQuery,
  globalQueryUrlParamNames,
} from '../navigation'
import type { StaticEvent, StaticGroup, StaticData, StaticPage, ShortcutFilterConfig } from '../types'
import { faAngleDown, faAngleLeft, faAngleRight, faFilter, faMagnifyingGlass, faSliders } from '@fortawesome/free-solid-svg-icons'
import { environment } from '../environment'
import css from './home.css'
import { searchEventsByQuery, searchGuestsByQuery } from '../services/search-service'
import { eventsSortingFunctions, guestsSortingFunctions } from '../services/sorting-service'
import { exhaustiveCheck, isNotNullish } from '../../../services/typescript'
import { formatDayShort, getDateDay, nowMs } from '../../../services/date'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { GuestsCompactList } from './guests-compact'
import { InfosPratiquesList } from './infos-pratiques'
import { PartenairesList } from './partenaires'
import { groupTagsByCategory } from '../../../services/tags'

type Filter = {
  genreIds: GenreId[]
  sceneIds: SceneId[]
  programIds: ProgramId[]
}

export const HomePage: React.FC<{ data: StaticData; defaultView: number }> = ({ data, defaultView }) => {
  const { t } = useTranslation()
  const config = useContext(MainConfig)
  const { events, groups, pages } = data

  const { search: searchParams, setUrl } = useContext(Navigation)
  const view = +(searchParams.get(globalQueryUrlParamNames['view']) ?? defaultView)
  const page = pages.at(view)
  const isGuestView = page?.view === 'GUESTS' || page?.view === 'GUESTS_COMPACT'
  const isSimpleView = page?.view === 'INFOS_PRATIQUES' || page?.view === 'PARTENAIRES'
  const pageSchedule: PageScheduleSettings = useMemo(
    () => ({
      alwaysShowEventHour: page?.alwaysShowEventHour,
      showEventHourDelay: page?.showEventHourDelay,
      eventsHideEndDate: page?.eventsHideEndDate,
      orderEventsByEndDate: page?.orderEventsByEndDate,
    }),
    [page?.alwaysShowEventHour, page?.showEventHourDelay, page?.eventsHideEndDate, page?.orderEventsByEndDate]
  )
  const availableDates: DateDay[] = useMemo(() => {
    if (isGuestView && page?.showEventDates) {
      const guestTypeIds = page.guestTypes ?? []
      const relevantGroups = guestTypeIds.length ? groups.filter((g) => guestTypeIds.includes(g.guestType._id)) : groups
      return [...new Set(relevantGroups.flatMap((g) => g.relatedEvents.map((ev) => getDateDay(ev.showStartDate, config))))].sort(
        (d1, d2) => new Date(d1).getTime() - new Date(d2).getTime()
      )
    }
    return [...new Set(events.filter((e) => page?.programs?.includes(e.programId)).map((ev) => getDateDay(ev.showStartDate, config)))].sort(
      (d1, d2) => new Date(d1).getTime() - new Date(d2).getTime()
    )
  }, [events, groups, page?.programs, page?.guestTypes, page?.showEventDates, isGuestView, config])

  const [query, setQuery] = useState<GlobalQuery>(loadGlobalQuery(config, data, searchParams, defaultView, availableDates))
  const appliedDaySelectionRef = useRef<{ view: number; defaultDaySelection: string } | null>(null)

  useEffect(() => {
    if (environment.isBackoffice) {
      setQuery((q) => ({ ...q, view: defaultView }))
    }
  }, [defaultView])
  useEffect(() => setUrl(getURLParamsForGlobalQuery(config, query, defaultView), 'replace'))

  // Apply page's defaultDaySelection when the view changes, or when the
  // page config itself updates (e.g. BO preview saves a new default).
  useEffect(() => {
    if (!page || !availableDates.length) return
    const defaultDaySelection = page.defaultDaySelection ?? 'closest'
    const last = appliedDaySelectionRef.current
    if (last && last.view === query.view && last.defaultDaySelection === defaultDaySelection) return
    appliedDaySelectionRef.current = { view: query.view, defaultDaySelection }

    if (defaultDaySelection === 'none') {
      setQuery((q) => ({ ...q, viewDate: null }))
    } else {
      // Default: 'closest' - find the closest date to today
      const targetViewDate = getDateDay(nowMs(), config)
      const closestDate = availableDates.reduce(
        (acc, date) =>
          Math.abs(new Date(acc).getTime() - new Date(targetViewDate).getTime()) <
          Math.abs(new Date(date).getTime() - new Date(targetViewDate).getTime())
            ? acc
            : date,
        availableDates[0]
      )
      setQuery((q) => ({ ...q, viewDate: closestDate }))
    }
  }, [page, availableDates, query.view, config])

  const [filtersVisible, setFiltersVisible] = useState<boolean>(false)

  const visibleDayCount: number = 5

  const [firstDayOffset, setFirstDayOffset] = useState<number>(Math.max(0, Math.min(availableDates.length - visibleDayCount, 0)))
  const visibleDates: DateDay[] = useMemo(
    () => availableDates.slice(firstDayOffset, Math.min(availableDates.length, firstDayOffset + visibleDayCount)),
    [availableDates, firstDayOffset]
  )
  const canAdvanceVisibleDays = firstDayOffset < availableDates.length - visibleDayCount
  const canStepBackVisibleDays = firstDayOffset > 0
  const advanceVisibleDays: (() => void) | undefined = !canAdvanceVisibleDays
    ? undefined
    : () => setFirstDayOffset(Math.max(0, Math.min(availableDates.length - visibleDayCount, firstDayOffset + visibleDayCount)))
  const stepBackVisibleDays: (() => void) | undefined = !canStepBackVisibleDays
    ? undefined
    : () => setFirstDayOffset(Math.max(0, firstDayOffset - visibleDayCount))

  const areAnyFilterSet: boolean = !!query.filterPlaces.length || !!query.filterGenres.length
  const resetFilters = (): void => setQuery({ ...query, filterPlaces: [], filterGenres: [], filterPrograms: [], searchQuery: '' })
  const toggleFilterByScene = (sceneId: SceneId): void => {
    const newPlaces = query.filterPlaces.includes(sceneId) ? query.filterPlaces.filter((s) => s !== sceneId) : [...query.filterPlaces, sceneId]
    setQuery({ ...query, filterPlaces: newPlaces })
  }
  const toggleFilterByGenre = (genreId: GenreId): void => {
    const newGenres = query.filterGenres.includes(genreId) ? query.filterGenres.filter((g) => g !== genreId) : [...query.filterGenres, genreId]
    setQuery({ ...query, filterGenres: newGenres })
  }
  const toggleFilterByProgram = (programId: ProgramId): void => {
    const newPrograms = query.filterPrograms.includes(programId)
      ? query.filterPrograms.filter((p) => p !== programId)
      : [...query.filterPrograms, programId]
    setQuery({ ...query, filterPrograms: newPrograms })
  }
  const setFilterByText = (searchQuery: string): void => setQuery({ ...query, searchQuery })
  const setSelectedView = (view: number): void => setQuery({ ...query, view })
  const setEventsSortOrder = (sortOrder: EventsSortOrder): void => setQuery({ ...query, eventsSortOrder: sortOrder })
  const setGuestsSortOrder = (sortOrder: GuestSortOrder): void => setQuery({ ...query, guestsSortOrder: sortOrder })
  const toggleSelectedDay = (day: DateDay): void => setQuery({ ...query, viewDate: query.viewDate === day ? null : day })

  if (!page) return null

  // get filters only on events/guests that can be shown
  const filters = getFilterIdsForView(page, events, groups)
  const filteredEvents: StaticEvent[] =
    page.view === 'EVENTS' || page.view === 'TIMELINE' ? filterEventsByQuery(events, query, page.programs ?? [], config, pageSchedule) : []
  const filteredGuests: StaticGroup[] =
    page.view === 'GUESTS' || page.view === 'GUESTS_COMPACT'
      ? filterGuestsByQuery(groups, query, page.guestTypes, page.showEventDates ? config : undefined)
      : []

  return (
    <PageScheduleConfig.Provider value={pageSchedule}>
      <div className={css.header}>
        <ViewSwitchDropDown selectedView={query.view} pages={pages} setSelectedView={setSelectedView} />
        <Title level={1} style={{ textAlign: 'center' }} className={css.title}>
          {page.blockName}
        </Title>
        <LangSwitchDropdown className={css.langSwitchDropdown} style={{ width: 'min-content', justifySelf: 'end' }} />
        {!isSimpleView && <SearchInput query={query} setFilterByText={setFilterByText} />}
        {!isSimpleView &&
          (page.view === 'EVENTS' ||
            page.view === 'TIMELINE' ||
            ((page.view === 'GUESTS' || page.view === 'GUESTS_COMPACT') && page.showEventDates)) &&
          visibleDates.length > 0 && (
            <DaySelector
              currentDate={query.viewDate}
              advanceVisibleDays={advanceVisibleDays}
              toggleSelectedDay={toggleSelectedDay}
              stepBackVisibleDays={stepBackVisibleDays}
              visibleDates={visibleDates}
            />
          )}
        {!isSimpleView && (
          <div className={css.buttonsBox}>
            <SortOrderDropdown
              query={query}
              sortedItems={page.view === 'TIMELINE' ? 'none' : page.view === 'EVENTS' ? 'events' : 'guests'}
              setEventsSortOrder={setEventsSortOrder}
              setGuestsSortOrder={setGuestsSortOrder}
            />
            <FiltersToggleButton areAnyFiltersSet={areAnyFilterSet} filtersVisible={filtersVisible} setFiltersVisible={setFiltersVisible} />
          </div>
        )}
        {/* Shortcut filters - quick access tag/scene/program filters */}
        {!isSimpleView && page.shortcutFilters && page.shortcutFilters.length > 0 && (
          <ShortcutFilters
            shortcutFilters={page.shortcutFilters}
            genres={data.genres}
            scenes={data.scenes}
            programs={data.programs}
            selectedGenreIds={query.filterGenres}
            selectedSceneIds={query.filterPlaces}
            selectedProgramIds={query.filterPrograms}
            toggleFilterByGenre={toggleFilterByGenre}
            toggleFilterByScene={toggleFilterByScene}
            toggleFilterByProgram={toggleFilterByProgram}
          />
        )}
      </div>

      {/* Filters block when unfold */}
      {!isSimpleView && filtersVisible && (
        <div style={styles.filtersHeader}>
          <div style={styles.filtersContainer}>
            <FiltersPanel
              currentViewItems={page.view}
              data={data}
              filters={filters}
              query={query}
              resetFilters={resetFilters}
              toggleFilterByGenre={toggleFilterByGenre}
              toggleFilterByProgram={toggleFilterByProgram}
              toggleFilterByScene={toggleFilterByScene}
            />
          </div>
        </div>
      )}

      {/* Actual page */}
      <div style={styles.pageContent}>
        {(page.view === 'EVENTS' || page.view === 'TIMELINE') && filteredEvents.length === 0 ? (
          <div style={styles.noEventsContainer}>
            <Title level={2} style={{ marginBottom: 10 }}>
              {t('HOME.NO_EVENTS_FOR_FILTERS')}
            </Title>
            <IconBtn onClick={resetFilters}>{t('HOME.RESET_FILTERS')}</IconBtn>
          </div>
        ) : (page.view === 'GUESTS' || page.view === 'GUESTS_COMPACT') && filteredGuests.length === 0 ? (
          <div style={styles.noEventsContainer}>
            <Title level={2} style={{ marginBottom: 10 }}>
              {t('HOME.NO_GUESTS_FOR_FILTER')}
            </Title>
            <IconBtn onClick={resetFilters}>{t('HOME.RESET_FILTERS')}</IconBtn>
          </div>
        ) : page.view === 'EVENTS' ? (
          <EventsPresentationLists
            events={filteredEvents}
            cardsPerRow={page.cardsPerRow ?? 4}
            scenesHeaders={
              query.filterPlaces.length === 1
                ? 'large'
                : query.eventsSortOrder !== 'place'
                ? 'none'
                : query.filterPlaces.length > 0
                ? 'large'
                : 'normal'
            }
            tileLayout={page.tileLayout}
          />
        ) : page.view === 'TIMELINE' ? (
          <EventsTimelineVue events={filteredEvents} date={query.viewDate} timezone={config.timezone} />
        ) : page.view === 'GUESTS' ? (
          <GuestsPresentationList guests={filteredGuests} cardsPerRow={page.cardsPerRow ?? 4} tileLayout={page.tileLayout} />
        ) : page.view === 'GUESTS_COMPACT' ? (
          <GuestsCompactList guests={filteredGuests} cardsPerRow={page.cardsPerRow ?? 1} tileLayout={page.tileLayout} />
        ) : page.view === 'INFOS_PRATIQUES' ? (
          <InfosPratiquesList
            infoPratiques={
              page.infoPratiqueIds?.length ? data.infoPratiques.filter((ip) => page.infoPratiqueIds!.includes(ip._id)) : data.infoPratiques
            }
            defaultExpanded={page.infoPratiquesExpanded}
            hideImages={page.infoPratiquesHideImages}
          />
        ) : page.view === 'PARTENAIRES' ? (
          <PartenairesList
            sponsors={data.sponsors}
            sponsorCategories={data.sponsorCategories}
            cardsPerRow={page.cardsPerRow}
            centerIcons={page.centerIcons}
          />
        ) : (
          exhaustiveCheck(page.view)
        )}
      </div>
    </PageScheduleConfig.Provider>
  )
}

function filterGuestsByQuery(groups: StaticGroup[], query: GlobalQuery, guestTypes: GuestTypeId[], staticCfg?: StaticMainConfig): StaticGroup[] {
  if (guestTypes.length) groups = groups.filter((g) => guestTypes.includes(g.guestType._id))
  if (query.viewDate && staticCfg) {
    groups = groups.filter((g) => g.relatedEvents.some((ev) => getDateDay(ev.showStartDate, { timezone: staticCfg.timezone }) === query.viewDate))
  }
  if (query.filterGenres.length) groups = groups.filter((g) => g.genres?.some((genre) => query.filterGenres.includes(genre._id)))
  if (query.filterPlaces.length) groups = groups.filter((g) => g.relatedEvents.some((ev) => ev.scene && query.filterPlaces.includes(ev.scene._id)))
  if (query.filterPrograms.length) groups = groups.filter((g) => g.relatedEvents.some((ev) => query.filterPrograms.includes(ev.programId)))
  if (query.searchQuery.length) groups = searchGuestsByQuery(query.searchQuery, groups)
  groups.sort(guestsSortingFunctions[query.guestsSortOrder])
  return groups
}

function getFilterIdsForView(page: StaticPage, events: StaticEvent[], groups: StaticGroup[]): Filter {
  if (page.view === 'INFOS_PRATIQUES' || page.view === 'PARTENAIRES') {
    return { genreIds: [], sceneIds: [], programIds: [] }
  }
  if (page.view === 'EVENTS' || page.view === 'TIMELINE') {
    const visibleEvents = page.programs?.length ? (events = events.filter((p) => page.programs?.includes(p.programId))) : events
    return {
      genreIds: [...new Set(visibleEvents.flatMap((event) => event.genres.map((genre) => genre._id)))],
      sceneIds: [...new Set(visibleEvents.map((event) => event.scene?._id).filter(isNotNullish))],
      programIds: [...new Set(visibleEvents.map((event) => event.programId))],
    }
  }
  const visibleGroups = page.guestTypes.length ? (groups = groups.filter((g) => page.guestTypes.includes(g.guestType._id))) : groups
  return {
    genreIds: [...new Set(visibleGroups.flatMap((group) => group.genres.map((genre) => genre._id)))],
    sceneIds: page.showEventScenes
      ? [...new Set(visibleGroups.flatMap((group) => group.relatedEvents.map((ev) => ev.scene?._id).filter(isNotNullish)))]
      : [],
    programIds: [],
  }
}

function filterEventsByQuery(
  events: StaticEvent[],
  query: GlobalQuery,
  programIds: ProgramId[],
  staticCfg: StaticMainConfig,
  schedule: PageScheduleSettings
): StaticEvent[] {
  if (programIds.length) events = events.filter((e) => programIds.includes(e.programId))
  // Only filter by date if a day is selected
  if (query.viewDate) {
    events = events.filter((ev) => getDateDay(ev.showStartDate, { timezone: staticCfg.timezone }) === query.viewDate)
  }
  if (query.filterPlaces.length) events = events.filter((ev) => ev.scene && query.filterPlaces.includes(ev.scene?._id))
  if (query.filterGenres.length) events = events.filter((ev) => ev.genres.some((genre) => query.filterGenres.includes(genre._id)))
  if (query.filterPrograms.length) events = events.filter((ev) => query.filterPrograms.includes(ev.programId))
  if (query.searchQuery.length) events = searchEventsByQuery(query.searchQuery, events)
  events.sort((a, b) => eventsSortingFunctions[query.eventsSortOrder](a, b, schedule))
  return events
}

// use to wrap Antd components that are not in blocks (block background style vs widget background style)
export const WidgetBackgroundStyle: React.FC<React.PropsWithChildren> = ({ children }) => {
  const config = useContext(MainConfig)

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgBase: config.colorAction,
          colorBorder: 'transparent',
          controlItemBgActive: 'rgb(1,1,1,.2)',
          colorTextBase: config.colorBackground,
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

// Variant for multi-select dropdowns (shortcut filters) - ensures placeholder text is visible
const WidgetMultiSelectStyle: React.FC<React.PropsWithChildren> = ({ children }) => {
  const config = useContext(MainConfig)

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgBase: config.colorAction,
          colorBorder: 'transparent',
          controlItemBgActive: 'rgb(1,1,1,.2)',
          colorTextBase: config.colorBackground,
          colorText: config.colorBackground,
          colorTextPlaceholder: config.colorBackground,
          colorTextQuaternary: config.colorBackground,
          colorIcon: config.colorBackground,
          colorIconHover: config.colorBackground,
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

const ViewSwitchDropDown: React.FC<{
  selectedView: number
  pages: StaticPage[]
  setSelectedView: (view: number) => void
}> = ({ selectedView, pages, setSelectedView }) => {
  const icon = (icon: IconProp): React.ReactElement => <Icon style={{ marginRight: 5 }} color={MainStylesVars.colorBackgroundOpaque} icon={icon} />

  type ViewItem = { view: HomePageView; index: number; name: string; icon: React.ReactElement }
  const viewItems: ViewItem[] = pages.filter((p) => p.visible).map((v, i) => ({ view: v.view, index: i, name: v.blockName, icon: icon(v.icon) }))

  return viewItems.length <= 1 ? (
    <></>
  ) : (
    <WidgetBackgroundStyle>
      <Dropdown
        className={css.viewSwitchDropDown}
        trigger={['click']}
        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
        menu={{
          items: viewItems.map(({ name, icon, index }) => ({
            label: name,
            icon,
            key: index,
            onClick: () => setSelectedView(index),
          })),
        }}
      >
        <Button type="primary" style={{ ...styles.primaryButton, display: 'flex', alignItems: 'center', width: 'min-content' }}>
          {viewItems.at(selectedView)?.icon}
          <Icon
            color={MainStylesVars.colorBackgroundOpaque}
            style={{ transform: 'translateY(1px)', pointerEvents: 'none', userSelect: 'none' }}
            icon={faAngleDown}
          />
        </Button>
      </Dropdown>
    </WidgetBackgroundStyle>
  )
}

const SearchInput: React.FC<{ query: GlobalQuery; setFilterByText: (text: string) => void }> = ({ query, setFilterByText }) => {
  const { t } = useTranslation()
  const config = useContext(MainConfig)

  return (
    <ConfigProvider theme={{ token: { colorTextBase: config.colorAction } }}>
      <Input
        className={css.searchInput}
        bordered={false}
        style={styles.filterInputText}
        classNames={{ input: css.filterInputTextInput }}
        placeholder={t('GENERAL.SEARCH')}
        onChange={(ev) => setFilterByText(ev.target.value)}
        value={query.searchQuery}
        prefix={<Icon icon={faMagnifyingGlass} color={MainStylesVars.colorAction} />}
      />
    </ConfigProvider>
  )
}

const FiltersToggleButton: React.FC<{ areAnyFiltersSet: boolean; filtersVisible: boolean; setFiltersVisible: (visible: boolean) => void }> = ({
  areAnyFiltersSet,
  setFiltersVisible,
  filtersVisible,
}) => {
  const { t } = useTranslation()

  return (
    <IconBtn
      className={css.filtersToggleButton}
      icon={faSliders}
      badge={areAnyFiltersSet}
      onClick={() => setFiltersVisible(!filtersVisible)}
      style={{ ...styles.primaryButton, ...styles.filtersToggleButton, ...(filtersVisible ? styles.filterToggledToggleButton : {}) }}
    >
      {t('GENERAL.FILTER')}
      <Icon
        icon={faAngleDown}
        style={{
          transform: `rotate(${filtersVisible ? -180 : 0}deg) translateY(1px)`,
          ...styles.filterArrowIcon,
        }}
        color={MainStylesVars.colorBackgroundOpaque}
      />
    </IconBtn>
  )
}

const DaySelector: React.FC<{
  visibleDates: DateDay[]
  stepBackVisibleDays: (() => void) | undefined
  advanceVisibleDays: (() => void) | undefined
  toggleSelectedDay: (day: DateDay) => void
  currentDate: DateDay | null
}> = ({ visibleDates, stepBackVisibleDays, advanceVisibleDays, toggleSelectedDay, currentDate }) => {
  const config = useContext(MainConfig)

  return (
    <div style={styles.daysPickerWrapper} className={css.daySelector}>
      <div style={styles.daysPickerContainer}>
        <span style={{ cursor: stepBackVisibleDays ? 'pointer' : undefined }} onClick={stepBackVisibleDays}>
          <Icon style={{ height: 24 }} icon={faAngleLeft} color={stepBackVisibleDays ? config.colorAction : config.colorBackground} />
        </span>
        <div style={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
          {visibleDates.map((d) => (
            <h3
              key={d}
              style={{ ...styles.dayBtn, color: d === currentDate ? MainStylesVars.colorAction : MainStylesVars.colorTitle }}
              className={css.daySelectorBtn}
              onClick={() => toggleSelectedDay(d)}
            >
              <ShortDateLines date={d} timezone={config.timezone} />
            </h3>
          ))}
        </div>
        <span style={{ cursor: advanceVisibleDays ? 'pointer' : undefined }} onClick={advanceVisibleDays}>
          <Icon style={{ height: 24 }} icon={faAngleRight} color={advanceVisibleDays ? config.colorAction : config.colorBackground} />
        </span>
      </div>
    </div>
  )
}

const ShortDateLines: React.FC<{ date: DateDay; timezone: string }> = ({ date, timezone }) => {
  const lang = useLang()
  const [l1, ...l2] = formatDayShort(date, { lang, timezone }).toUpperCase().split(' ')
  return (
    <>
      {[l1, l2.join(' ')].map((l, i) => (
        <div key={i} style={{ whiteSpace: 'nowrap', fontSize: i === 0 ? undefined : '1rem' }}>
          {l}
        </div>
      ))}
    </>
  )
}

const SortOrderDropdown: React.FC<{
  query: GlobalQuery
  sortedItems: 'events' | 'guests' | 'none'
  setEventsSortOrder: (sortOrder: EventsSortOrder) => void
  setGuestsSortOrder: (sortOrder: GuestSortOrder) => void
}> = ({ query, sortedItems, setEventsSortOrder, setGuestsSortOrder }) => {
  const { t } = useTranslation()

  return (
    <WidgetBackgroundStyle>
      {sortedItems === 'events' ? (
        <Select<EventsSortOrder>
          suffixIcon={<Icon icon={faFilter} color={MainStylesVars.colorBackgroundOpaque} style={{ pointerEvents: 'none', userSelect: 'none' }} />}
          value={query.eventsSortOrder}
          onChange={setEventsSortOrder}
          options={[
            { value: 'time', label: t('GENERAL.PASSAGE_TIMES') },
            { value: 'relevance', label: t('GENERAL.RELEVANCE') },
            { value: 'place', label: t('GENERAL.PLACES') },
            { value: 'alphabetical', label: t('GENERAL.ALPHABETICAL_ORDER') },
          ]}
          className={css.sortOrderDropdown}
          popupMatchSelectWidth={false}
          getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
        />
      ) : sortedItems === 'guests' ? (
        <Select<GuestSortOrder>
          suffixIcon={<Icon icon={faFilter} color={MainStylesVars.colorBackgroundOpaque} style={{ pointerEvents: 'none', userSelect: 'none' }} />}
          value={query.guestsSortOrder}
          onChange={setGuestsSortOrder}
          options={[
            { value: 'relevance', label: t('GENERAL.RELEVANCE') },
            { value: 'alphabetical', label: t('GENERAL.ALPHABETICAL_ORDER') },
          ]}
          className={css.sortOrderDropdown}
          popupMatchSelectWidth={false}
          getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
        />
      ) : (
        <></>
      )}
    </WidgetBackgroundStyle>
  )
}

const FiltersPanel: React.FC<{
  currentViewItems: HomePageView
  toggleFilterByProgram: (id: ProgramId) => void
  toggleFilterByScene: (id: SceneId) => void
  toggleFilterByGenre: (id: GenreId) => void
  resetFilters: () => void
  query: GlobalQuery
  data: StaticData
  filters: Filter
}> = ({ currentViewItems, toggleFilterByGenre, toggleFilterByProgram, toggleFilterByScene, resetFilters, query, data, filters }) => {
  const { t } = useTranslation()
  const { programs, scenes, genres } = {
    programs: data.programs.filter((program) => filters.programIds.includes(program._id)),
    scenes: data.scenes.filter((scene) => filters.sceneIds.includes(scene._id)),
    genres: data.genres.filter((genre) => filters.genreIds.includes(genre._id)),
  }

  return (
    <>
      {(currentViewItems === 'EVENTS' || currentViewItems === 'TIMELINE') && programs.length > 1 && (
        <>
          <h3 style={styles.filtersListTitle}>{t('HOME.FILTERS.PROGRAMS')}</h3>
          <div style={styles.filtersSection}>
            {programs.map((program) => (
              <Button
                style={query.filterPrograms.includes(program._id) ? styles.filtersButtonSelected : styles.filtersButtonUnselected}
                key={program._id}
                shape="round"
                size="small"
                onClick={() => toggleFilterByProgram(program._id)}
              >
                {program.name}
              </Button>
            ))}
          </div>
        </>
      )}
      {scenes.length > 1 && (
        <>
          <h3 style={styles.filtersListTitle}>{t('HOME.FILTERS.PLACE')}</h3>
          <div style={styles.filtersSection}>
            {scenes.map((scene) => (
              <Button
                key={scene._id}
                style={query.filterPlaces.includes(scene._id) ? styles.filtersButtonSelected : styles.filtersButtonUnselected}
                shape="round"
                size="small"
                onClick={() => toggleFilterByScene(scene._id)}
              >
                {scene.name}
              </Button>
            ))}
          </div>
        </>
      )}
      {genres.length > 1 && <GenreFilters genres={genres} query={query} toggleFilterByGenre={toggleFilterByGenre} />}
      <div style={{ display: 'flex', justifyContent: 'end', marginTop: 8 }}>
        <IconBtn onClick={resetFilters}>{t('HOME.RESET_FILTERS')}</IconBtn>
      </div>
    </>
  )
}

const GenreFilters: React.FC<{
  genres: Genre[]
  query: GlobalQuery
  toggleFilterByGenre: (id: GenreId) => void
}> = ({ genres, query, toggleFilterByGenre }) => {
  const lang = useLang()
  const groupedGenres = groupTagsByCategory(genres, { lang }, true)

  return (
    <>
      {groupedGenres.map(({ category, genres: categoryGenres, key }: { category: Category; genres: Genre[]; key: string }) => (
        <div key={key}>
          <h3 style={styles.filtersListTitle}>{category.name}</h3>
          <div style={styles.filtersSection}>
            {categoryGenres.map((genre) => (
              <Button
                key={genre._id}
                style={query.filterGenres.includes(genre._id) ? styles.filtersButtonSelected : styles.filtersButtonUnselected}
                shape="round"
                size="small"
                onClick={() => toggleFilterByGenre(genre._id)}
              >
                {genre.name}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

const ShortcutFilterGroup: React.FC<{
  items: { _id: string; name: string }[]
  selectedIds: string[]
  toggleFilter: (id: string) => void
  displayMode?: 'buttons' | 'dropdown'
  placeholder: string
}> = ({ items, selectedIds, toggleFilter, displayMode, placeholder }) => {
  if (items.length === 0) return null

  if (displayMode === 'dropdown') {
    const selectedValues = items.filter((item) => selectedIds.includes(item._id)).map((item) => item._id)
    return (
      <WidgetMultiSelectStyle>
        <Select
          mode="multiple"
          showSearch={false}
          value={selectedValues}
          onChange={(values: string[]) => {
            const prev = new Set(selectedValues)
            const next = new Set(values)
            for (const id of values) {
              if (!prev.has(id)) toggleFilter(id)
            }
            for (const id of selectedValues) {
              if (!next.has(id)) toggleFilter(id)
            }
          }}
          options={items.map((item) => ({ value: item._id, label: item.name }))}
          placeholder={placeholder}
          className={css.shortcutFilterDropdown}
          style={{ minWidth: `${placeholder.length + 6}ch` }}
          allowClear
          onClear={() => {
            selectedValues.forEach((id) => toggleFilter(id))
          }}
          popupMatchSelectWidth={false}
          maxTagCount={1}
          getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
        />
      </WidgetMultiSelectStyle>
    )
  }

  return items.map((item) => (
    <Button
      key={item._id}
      style={selectedIds.includes(item._id) ? styles.shortcutChipSelected : styles.shortcutChipUnselected}
      shape="round"
      size="small"
      onClick={() => toggleFilter(item._id)}
    >
      {item.name}
    </Button>
  ))
}

const ShortcutFilters: React.FC<{
  shortcutFilters: ShortcutFilterConfig[]
  genres: Genre[]
  scenes: Scene[]
  programs: Program[]
  selectedGenreIds: GenreId[]
  selectedSceneIds: SceneId[]
  selectedProgramIds: ProgramId[]
  toggleFilterByGenre: (id: GenreId) => void
  toggleFilterByScene: (id: SceneId) => void
  toggleFilterByProgram: (id: ProgramId) => void
}> = ({
  shortcutFilters,
  genres,
  scenes,
  programs,
  selectedGenreIds,
  selectedSceneIds,
  selectedProgramIds,
  toggleFilterByGenre,
  toggleFilterByScene,
  toggleFilterByProgram,
}) => {
  return (
    <div className={css.shortcutFiltersContainer} style={styles.shortcutFiltersContainer}>
      {shortcutFilters.map((filter, i) => {
        if (filter.type === 'scenes') {
          const filterScenes = filter.tagIds.length ? filter.tagIds.map((id) => scenes.find((s) => s._id === id)).filter(isNotNullish) : scenes
          return (
            <ShortcutFilterGroup
              key={i}
              items={filterScenes}
              selectedIds={selectedSceneIds}
              toggleFilter={toggleFilterByScene as (id: string) => void}
              displayMode={filter.displayMode}
              placeholder={filter.label ?? filter.categoryName}
            />
          )
        }

        if (filter.type === 'programs') {
          const filterPrograms = filter.tagIds.length ? filter.tagIds.map((id) => programs.find((p) => p._id === id)).filter(isNotNullish) : programs
          return (
            <ShortcutFilterGroup
              key={i}
              items={filterPrograms}
              selectedIds={selectedProgramIds}
              toggleFilter={toggleFilterByProgram as (id: string) => void}
              displayMode={filter.displayMode}
              placeholder={filter.label ?? filter.categoryName}
            />
          )
        }

        const filterGenres = filter.tagIds.length
          ? filter.tagIds.map((tagId) => genres.find((g) => g._id === tagId)).filter(isNotNullish)
          : genres.filter((g) => g.category?.name === filter.categoryName)
        return (
          <ShortcutFilterGroup
            key={i}
            items={filterGenres}
            selectedIds={selectedGenreIds}
            toggleFilter={toggleFilterByGenre as (id: string) => void}
            displayMode={filter.displayMode}
            placeholder={filter.label ?? filter.categoryName}
          />
        )
      })}
    </div>
  )
}

const styles = StyleSheet.create({
  filtersRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  filterArrowIcon: {
    transition: '.5s transform',
    paddingLeft: 8,
    pointerEvents: 'none',
    userSelect: 'none',
    position: 'absolute',
    right: 10,
  },

  filtersHeader: {
    flexShrink: 0,
    marginBottom: 5,
  },

  filtersContainer: {
    backgroundColor: MainStylesVars.colorBlockBackground,
    borderRadius: 10,
    padding: 15,
  },

  filtersListTitle: {
    marginTop: 24,
    marginBottom: 12,
  },

  filtersSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    color: MainStylesVars.colorBlockAction,
  },

  filterInputText: {
    borderBottom: `2px solid ${MainStylesVars.colorAction}`,
    borderRadius: 0,
    height: 'min-content',
  },

  primaryButton: {
    backgroundColor: MainStylesVars.colorAction,
    color: MainStylesVars.colorBackgroundOpaque,
    boxShadow: 'none',
  },

  filtersToggleButton: {
    paddingRight: 40,
    display: 'flex',
    justifyContent: 'center',
  },

  filterToggledToggleButton: {
    backgroundColor: MainStylesVars.colorBlockBackground,
  },

  daysPickerWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },

  daysPickerContainer: {
    display: 'flex',
    minHeight: '3rem',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    width: 'max-content',
    flexWrap: 'nowrap',
    zIndex: 1,
  },

  dayBtn: {
    textAlign: 'center',
    userSelect: 'none',
    cursor: 'pointer',
    fontFamily: MainStylesVars.fontTitle,
  },

  pageContent: {
    marginTop: 20,
  },

  noEventsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    marginBottom: '10%',
  },

  filtersButtonUnselected: {
    color: MainStylesVars.colorBlockAction,
    borderColor: MainStylesVars.colorBlockAction,
    backgroundColor: MainStylesVars.colorBlockBackground,
  },

  filtersButtonSelected: {
    color: MainStylesVars.colorBlockBackground,
    borderColor: MainStylesVars.colorBlockAction,
    backgroundColor: MainStylesVars.colorBlockAction,
  },

  shortcutFiltersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    marginBottom: 4,
  },

  shortcutChipUnselected: {
    color: MainStylesVars.colorAction,
    borderColor: MainStylesVars.colorAction,
    backgroundColor: 'transparent',
  },

  shortcutChipSelected: {
    color: MainStylesVars.colorBackground,
    borderColor: MainStylesVars.colorAction,
    backgroundColor: MainStylesVars.colorAction,
  },
})
